/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { Annotations } from 'plotly.js';
import type { StoredPlotlyResponse } from '@/types/eeCheck';
import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import type { TimeSeriesTrafficLightConfig } from '@/lib/timeSeries/timeSeriesValue';

export type TimeSeriesPlotType = 'line' | 'stackedBar' | 'groupedBar';

export interface TimeSeriesBandMarker {
    activeValue?: number;
    color: string;
    label: string;
    maxValue?: number;
    maxValueExclusive?: number;
    minValue?: number;
    series: string;
    showSeries?: boolean;
}

export interface TimeSeriesValueBand {
    color: string;
    from?: number;
    to?: number;
}

export interface TimeSeriesTimeBand {
    borderColor?: string;
    color: string;
    from: number | string;
    to: number | string;
}

export interface TimeSeriesVerticalMarker {
    color?: string;
    legendUnit?: string;
    label: string;
    locale?: string;
    showInLegend?: boolean;
    showValueInAnnotation?: boolean;
    showValueGuide?: boolean;
    timestamp: number | string;
    valueSelection?: 'next' | 'previous';
}

export interface TimeSeriesPlotTrafficLightLegendConfig {
    fractionDigits?: number;
    title?: string;
    trafficLight: TimeSeriesTrafficLightConfig;
    unit?: string | null;
}

export type TimeSeriesLegendNames = Record<string, string>;
export type TimeSeriesTraceColors = Record<string, string>;

function matchesSeriesColumn(column: string, series: string): boolean {
    return column === series || column.startsWith(`${series} [`);
}

function isRightAxisColumn(column: string, rightAxisSeries: string[]): boolean {
    return rightAxisSeries.some((series) => matchesSeriesColumn(column, series));
}

function getBandMarker(
    column: string,
    bandMarkers: TimeSeriesBandMarker[],
): TimeSeriesBandMarker | undefined {
    return bandMarkers.find((marker) => matchesSeriesColumn(column, marker.series));
}

function resolveLegendName(
    column: string,
    legendNames: TimeSeriesLegendNames,
): string {
    const matchingKey = Object.keys(legendNames).find((series) => matchesSeriesColumn(column, series));

    return matchingKey ? legendNames[matchingKey] ?? column : column;
}

function resolveTraceColor(
    column: string,
    traceColors: TimeSeriesTraceColors,
): string | undefined {
    const matchingKey = Object.keys(traceColors).find((series) => matchesSeriesColumn(column, series));

    return matchingKey ? traceColors[matchingKey] : undefined;
}

function isBandMarkerActive(
    value: number,
    marker: TimeSeriesBandMarker,
): boolean {
    if (!Number.isFinite(value)) {
        return false;
    }

    if (marker.activeValue !== undefined) {
        return value === marker.activeValue;
    }

    if (
        marker.minValue !== undefined
        || marker.maxValue !== undefined
        || marker.maxValueExclusive !== undefined
    ) {
        return (marker.minValue === undefined || value >= marker.minValue)
            && (marker.maxValue === undefined || value <= marker.maxValue)
            && (marker.maxValueExclusive === undefined || value < marker.maxValueExclusive);
    }

    return value === 1;
}

function resolveBoundaryTimestamps(
    values: string[],
    index: number,
): { end: number | string; start: number | string } {
    const currentValue = values[index] ?? '';
    const currentTimestamp = Date.parse(currentValue);

    if (Number.isNaN(currentTimestamp)) {
        return {
            start: currentValue,
            end: values[index + 1] ?? currentValue,
        };
    }

    const previousTimestamp =
        index > 0 ? Date.parse(values[index - 1] ?? '') : Number.NaN;
    const nextTimestamp =
        index < values.length - 1 ? Date.parse(values[index + 1] ?? '') : Number.NaN;

    const start = !Number.isNaN(previousTimestamp)
        ? previousTimestamp + (currentTimestamp - previousTimestamp) / 2
        : !Number.isNaN(nextTimestamp)
            ? currentTimestamp - (nextTimestamp - currentTimestamp) / 2
            : currentTimestamp;
    const end = !Number.isNaN(nextTimestamp)
        ? currentTimestamp + (nextTimestamp - currentTimestamp) / 2
        : !Number.isNaN(previousTimestamp)
            ? currentTimestamp + (currentTimestamp - previousTimestamp) / 2
            : currentTimestamp;

    return { start, end };
}

function createBandShapes(
    preview: ParsedTimeSeriesCsv,
    marker: TimeSeriesBandMarker,
): Array<Record<string, unknown>> {
    const markerColumn = preview.plotYAxisColumns.find((column) =>
        matchesSeriesColumn(column, marker.series),
    );

    if (!markerColumn) {
        return [];
    }

    const values = preview.plotYAxisValues[markerColumn] ?? [];
    const shapes: Array<Record<string, unknown>> = [];
    let activeStartIndex: number | null = null;

    values.forEach((value, index) => {
        const isActive = isBandMarkerActive(value, marker);

        if (isActive && activeStartIndex === null) {
            activeStartIndex = index;
            return;
        }

        if (!isActive && activeStartIndex !== null) {
            const startBoundary = resolveBoundaryTimestamps(
                preview.plotXAxisValues,
                activeStartIndex,
            );
            const endBoundary = resolveBoundaryTimestamps(
                preview.plotXAxisValues,
                index - 1,
            );

            shapes.push({
                type: 'rect',
                xref: 'x',
                yref: 'paper',
                x0: startBoundary.start,
                x1: endBoundary.end,
                y0: 0,
                y1: 1,
                fillcolor: marker.color,
                opacity: 1,
                line: {
                    width: 0,
                },
                layer: 'below',
            });

            activeStartIndex = null;
        }
    });

    if (activeStartIndex !== null) {
        const startBoundary = resolveBoundaryTimestamps(
            preview.plotXAxisValues,
            activeStartIndex,
        );
        const endBoundary = resolveBoundaryTimestamps(
            preview.plotXAxisValues,
            preview.plotXAxisValues.length - 1,
        );

        shapes.push({
            type: 'rect',
            xref: 'x',
            yref: 'paper',
            x0: startBoundary.start,
            x1: endBoundary.end,
            y0: 0,
            y1: 1,
            fillcolor: marker.color,
            opacity: 1,
            line: {
                width: 0,
            },
            layer: 'below',
        });
    }

    return shapes;
}

function createValueBandShapes(
    preview: ParsedTimeSeriesCsv,
    valueBands: TimeSeriesValueBand[],
): Array<Record<string, unknown>> {
    const column = preview.plotYAxisColumns[0];
    const values = column ? preview.plotYAxisValues[column] ?? [] : [];
    const boundaries = valueBands.flatMap((band) => [band.from, band.to])
        .filter((value): value is number => Number.isFinite(value));
    const finiteValues = values.filter((value) => Number.isFinite(value));

    if (finiteValues.length === 0 || boundaries.length === 0) {
        return [];
    }

    const lowerBoundary = Math.min(0, ...finiteValues, ...boundaries);
    const upperBoundary = Math.max(...finiteValues, ...boundaries);

    return valueBands.flatMap((band) => {
        const from = band.from ?? lowerBoundary;
        const to = band.to ?? upperBoundary;

        if (to <= from) {
            return [];
        }

        return [{
            type: 'rect',
            xref: 'paper',
            yref: 'y',
            x0: 0,
            x1: 1,
            y0: from,
            y1: to,
            fillcolor: band.color,
            line: { width: 0 },
            layer: 'below',
        }];
    });
}

function createTimeBandShapes(
    timeBands: TimeSeriesTimeBand[],
): Array<Record<string, unknown>> {
    return timeBands.map((band) => ({
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: band.from,
        x1: band.to,
        y0: 0,
        y1: 1,
        fillcolor: band.color,
        line: {
            color: band.borderColor ?? band.color,
            width: 1,
        },
        layer: 'below',
    }));
}

function createBandLegendTrace(
    preview: ParsedTimeSeriesCsv,
    marker: TimeSeriesBandMarker,
) {
    return {
        x: [preview.plotXAxisValues[0]],
        y: [0],
        type: 'scatter' as const,
        mode: 'markers' as const,
        marker: {
            size: 10,
            symbol: 'square',
            color: marker.color,
        },
        name: marker.label,
        hoverinfo: 'skip' as const,
        visible: 'legendonly' as const,
    };
}

function getTimestamp(value: number | string): number | null {
    const timestamp = typeof value === 'number' ? value : Date.parse(value);

    return Number.isNaN(timestamp) ? null : timestamp;
}

export function getNextVerticalMarkerValue(
    preview: ParsedTimeSeriesCsv,
    marker: TimeSeriesVerticalMarker,
): number | null {
    const markerTimestamp = getTimestamp(marker.timestamp);
    const column = preview.plotYAxisColumns[0];
    const values = column ? preview.plotYAxisValues[column] ?? [] : [];

    if (markerTimestamp === null) {
        return null;
    }

    let nextValue: { timestamp: number; value: number } | null = null;

    for (let index = 0; index < preview.plotXAxisValues.length; index += 1) {
        const timestamp = getTimestamp(preview.plotXAxisValues[index] ?? '');
        const value = values[index];

        if (
            timestamp === null ||
            timestamp < markerTimestamp ||
            !Number.isFinite(value) ||
            (nextValue !== null && timestamp >= nextValue.timestamp)
        ) {
            continue;
        }

        nextValue = { timestamp, value: value! };
    }

    return nextValue?.value ?? null;
}

function getPreviousVerticalMarkerValue(
    preview: ParsedTimeSeriesCsv,
    marker: TimeSeriesVerticalMarker,
): number | null {
    const markerTimestamp = getTimestamp(marker.timestamp);
    const column = preview.plotYAxisColumns[0];
    const values = column ? preview.plotYAxisValues[column] ?? [] : [];

    if (markerTimestamp === null) {
        return null;
    }

    let previousValue: { timestamp: number; value: number } | null = null;

    for (let index = 0; index < preview.plotXAxisValues.length; index += 1) {
        const timestamp = getTimestamp(preview.plotXAxisValues[index] ?? '');
        const value = values[index];

        if (
            timestamp === null ||
            timestamp >= markerTimestamp ||
            !Number.isFinite(value) ||
            (previousValue !== null && timestamp <= previousValue.timestamp)
        ) {
            continue;
        }

        previousValue = { timestamp, value: value! };
    }

    return previousValue?.value ?? null;
}

export function getVerticalMarkerValue(
    preview: ParsedTimeSeriesCsv,
    marker: TimeSeriesVerticalMarker,
): number | null {
    return marker.valueSelection === 'previous'
        ? getPreviousVerticalMarkerValue(preview, marker)
        : getNextVerticalMarkerValue(preview, marker);
}

function getVerticalMarkerValueWithUnit(
    preview: ParsedTimeSeriesCsv,
    marker: TimeSeriesVerticalMarker,
): string {
    const value = getVerticalMarkerValue(preview, marker);
    const formattedValue = value === null
        ? 'N/A'
        : new Intl.NumberFormat(marker.locale ?? 'de-DE', {
            maximumFractionDigits: 2,
        }).format(value);

    return marker.legendUnit
        ? `${formattedValue} ${marker.legendUnit}`
        : formattedValue;
}

function createVerticalMarkerLegendTrace(
    preview: ParsedTimeSeriesCsv,
    marker: TimeSeriesVerticalMarker,
) {
    const color = marker.color ?? '#d97706';
    const valueWithUnit = getVerticalMarkerValueWithUnit(preview, marker);

    return {
        x: [preview.plotXAxisValues[0]],
        y: [0],
        type: 'scatter' as const,
        mode: 'lines' as const,
        line: {
            color,
            dash: 'dot' as const,
            width: 2,
        },
        name: `${marker.label} (${valueWithUnit})`,
        hoverinfo: 'skip' as const,
        visible: 'legendonly' as const,
    };
}

interface TimeSeriesPlotlyFigureOptions {
    bandMarkers?: TimeSeriesBandMarker[];
    chartType?: TimeSeriesPlotType;
    includeZeroYAxis?: boolean;
    legendNames?: TimeSeriesLegendNames;
    traceColors?: TimeSeriesTraceColors;
    rightAxisSeries?: string[];
    timeBands?: TimeSeriesTimeBand[];
    title?: string;
    valueBands?: TimeSeriesValueBand[];
    verticalMarkers?: TimeSeriesVerticalMarker[];
    xAxisTitle?: string;
    xAxisRange?: [string, string];
    yAxisTitle?: string;
}

export function createTimeSeriesPlotlyFigure(
    preview: ParsedTimeSeriesCsv,
    {
        bandMarkers = [],
        chartType = 'line',
        includeZeroYAxis = false,
        legendNames = {},
        traceColors = {},
        rightAxisSeries = [],
        timeBands = [],
        title,
        valueBands = [],
        verticalMarkers = [],
        xAxisTitle,
        xAxisRange,
        yAxisTitle,
    }: TimeSeriesPlotlyFigureOptions = {},
): StoredPlotlyResponse | null {
    if (
        !preview.plotXAxisColumn ||
        preview.plotYAxisColumns.length === 0
    ) {
        return null;
    }

    const plotData = preview.plotYAxisColumns
        .map((column) => {
            const bandMarker = getBandMarker(column, bandMarkers);

            if (bandMarker && bandMarker.showSeries !== true) {
                return null;
            }

            const values = preview.plotYAxisValues[column] ?? [];

            if (values.length === 0) {
                return null;
            }

            const traceName = resolveLegendName(column, legendNames);
            const traceColor = resolveTraceColor(column, traceColors);

            return {
                x: [...preview.plotXAxisValues],
                y: [...values],
                type: chartType === 'line' ? ('scattergl' as const) : ('bar' as const),
                ...(chartType === 'line'
                    ? {
                        mode: 'lines' as const,
                        line: {
                            ...(traceColor ? { color: traceColor } : {}),
                            width: 1.5,
                        },
                    }
                    : traceColor
                        ? {
                            marker: {
                                color: traceColor,
                            },
                        }
                        : {}),
                yaxis: isRightAxisColumn(column, rightAxisSeries) ? 'y2' : 'y',
                hovertemplate: `%{x}<br>%{y}<extra>${traceName}</extra>`,
                name: traceName,
            };
        })
        .filter((trace) => trace !== null);

    const bandLegendTraces = bandMarkers
        .filter((marker) =>
            preview.plotYAxisColumns.some((column) => matchesSeriesColumn(column, marker.series)),
        )
        .map((marker) => createBandLegendTrace(preview, marker));
    const verticalMarkerLegendTraces = verticalMarkers
        .filter((marker) => marker.showInLegend === true)
        .map((marker) => createVerticalMarkerLegendTrace(preview, marker));
    const bandShapes = bandMarkers.flatMap((marker) => createBandShapes(preview, marker));
    const valueBandShapes = createValueBandShapes(preview, valueBands);
    const timeBandShapes = createTimeBandShapes(timeBands);
    const verticalMarkerShapes: Array<Record<string, unknown>> = verticalMarkers.map((marker) => ({
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: marker.timestamp,
        x1: marker.timestamp,
        y0: 0,
        y1: 1,
        line: {
            color: marker.color ?? '#d97706',
            dash: 'dot',
            width: 2,
        },
    }));
    const verticalMarkerValueGuideShapes: Array<Record<string, unknown>> = verticalMarkers.flatMap((marker) => {
        const value = getVerticalMarkerValue(preview, marker);

        if (marker.showValueGuide !== true || value === null) {
            return [];
        }

        return [{
            type: 'line',
            xref: 'paper',
            yref: 'y',
            x0: 0,
            x1: 1,
            y0: value,
            y1: value,
            line: {
                color: marker.color ?? '#d97706',
                dash: 'dot',
                width: 1,
            },
            opacity: 0.6,
        }];
    });
    const verticalMarkerAnnotations: Partial<Annotations>[] = verticalMarkers.map((marker) => ({
        x: marker.timestamp,
        xref: 'x',
        y: 1,
        yref: 'paper',
        text: marker.showValueInAnnotation
            ? `${marker.label} (${getVerticalMarkerValueWithUnit(preview, marker)})`
            : marker.label,
        showarrow: false,
        yanchor: 'bottom',
        font: {
            color: marker.color ?? '#d97706',
        },
    }));

    if (
        plotData.length === 0 &&
        bandLegendTraces.length === 0 &&
        verticalMarkerLegendTraces.length === 0 &&
        !xAxisRange
    ) {
        return null;
    }

    const primaryYAxisColumn = preview.plotYAxisColumns[0];
    const chartTitle =
        title ??
        (preview.plotYAxisColumns.length === 1
            ? primaryYAxisColumn
            : 'Time series');
    const resolvedYAxisTitle =
        yAxisTitle ??
        (preview.plotYAxisColumns.length === 1
            ? primaryYAxisColumn
            : 'Values');
    const rightAxisColumns = preview.plotYAxisColumns.filter((column) => {
        const bandMarker = getBandMarker(column, bandMarkers);

        return isRightAxisColumn(column, rightAxisSeries) && (!bandMarker || bandMarker.showSeries === true);
    });
    const rightAxisTitle =
        rightAxisColumns.length === 1
            ? rightAxisColumns[0]
            : rightAxisColumns.length > 1
              ? 'Values'
              : undefined;

    return {
        data: [...plotData, ...bandLegendTraces, ...verticalMarkerLegendTraces],
        layout: {
            autosize: true,
            title: {
                text: chartTitle,
            },
            margin: {
                t: 56,
                r: 24,
                b: 56,
                l: 72,
            },
            xaxis: {
                title: {
                    text: xAxisTitle ?? 'Period',
                },
                ...(xAxisRange ? { autorange: false, range: xAxisRange } : {}),
            },
            yaxis: {
                title: {
                    text: resolvedYAxisTitle,
                },
                side: 'left',
                automargin: true,
                ...(includeZeroYAxis ? { rangemode: 'tozero', zeroline: true } : {}),
            },
            ...(bandShapes.length > 0 || valueBandShapes.length > 0 || timeBandShapes.length > 0 || verticalMarkerShapes.length > 0 || verticalMarkerValueGuideShapes.length > 0
                ? { shapes: [...valueBandShapes, ...timeBandShapes, ...bandShapes, ...verticalMarkerShapes, ...verticalMarkerValueGuideShapes] }
                : {}),
            ...(verticalMarkerAnnotations.length > 0
                ? { annotations: verticalMarkerAnnotations }
                : {}),
            ...(chartType === 'stackedBar' ? { barmode: 'stack' as const } : {}),
            ...(chartType === 'groupedBar' ? { barmode: 'group' as const } : {}),
            ...(rightAxisColumns.length > 0
                ? {
                    yaxis2: {
                        title: {
                            text: rightAxisTitle,
                        },
                        anchor: 'free',
                        automargin: true,
                        overlaying: 'y',
                        position: 1,
                        side: 'right',
                        showgrid: false,
                    },
                }
                : {}),
        },
        config: {
            responsive: true,
            displaylogo: false,
        },
    };
}
